/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-29
 */

import {
  Container,
  Typography,
  Box,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppearanceSettings from './AppearanceSettings';
import AccountPreferencesSection from './AccountPreferencesSection';
import PrivacySettingsSection from './PrivacySettingsSection';
import LegalLinksSection from './LegalLinksSection';
import Header from '../../layout/Header';
import { useSessionStatusQuery } from '../../../features/auth';

/**
 * Main Settings page component that provides a centralized location for user preferences.
 * @returns Settings page component
 */
const Settings: React.FC = () => {
  const { t } = useTranslation('settings');
  const { data: sessionData, isLoading } = useSessionStatusQuery();
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const user = sessionData?.user;

  // Handle success/error notifications
  const handleUpdate = (message: string) => {
    setSuccessMessage(message);
  };

  const handleError = (error: string) => {
    setErrorMessage(error);
  };

  const handleCloseSuccess = () => {
    setSuccessMessage('');
  };

  const handleCloseError = () => {
    setErrorMessage('');
  };

  if (isLoading) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Typography>{t('loading')}</Typography>
        </Container>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error">{t('errorLoadingUser')}</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('description')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Account Preferences */}
          <Paper elevation={1} sx={{ p: 3, overflow: 'hidden' }}>
            <AccountPreferencesSection
              initialData={{
                marketingEmails: user.marketing_emails || false,
                timezone: user.timezone || 'UTC',
              }}
              onUpdate={handleUpdate}
              onError={handleError}
            />
          </Paper>

          {/* Privacy Settings */}
          <Paper elevation={1} sx={{ p: 3, overflow: 'hidden' }}>
            <PrivacySettingsSection
              initialData={{
                addressVisible: user.address_visible || false,
                emailVisible: user.email_visible || false,
                phoneVisible: user.phone_visible || false,
              }}
              onUpdate={handleUpdate}
              onError={handleError}
            />
          </Paper>

          {/* Appearance Settings */}
          <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
            <AppearanceSettings />
          </Paper>

          {/* Legal & Information Links */}
          <Paper elevation={1} sx={{ p: 3, overflow: 'hidden' }}>
            <LegalLinksSection />
          </Paper>
        </Box>
      </Container>

      {/* Success Notification */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSuccess}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
